import React from "react";
import {SearchIcon} from "../../assets/svgComponents/searchIcon";
import {DateSelectorMode, FlightSearchData} from "../../models/flightSearchData";
import {SelectorType} from "../../models/FlightSearch";
import {useNavigate} from "react-router-dom";
import {useFlightSearchStore} from "../../store/useFlightSearchStore";
import {DestinationCategory} from "../../models/DestinationCategory";
import {getCategoryLabel, useTranslation} from "../../i18n/LanguageProvider";
import { FiRepeat } from "react-icons/fi";

const SwapIcon = FiRepeat as unknown as React.FC;

interface FlightSearchBarProps {
    activeSelector: SelectorType | null;
    setActiveSelector: (selector: SelectorType | null) => void;
    searchData: FlightSearchData;
    setSearchData?: React.Dispatch<React.SetStateAction<FlightSearchData>>;
    onSearch?: () => void;
}

export const FlightSearchBar: React.FC<FlightSearchBarProps> = ({
                                                                    activeSelector,
                                                                    setActiveSelector,
                                                                    searchData,
                                                                    setSearchData,
                                                                    onSearch,
                                                                }) => {
    const navigate = useNavigate();
    const setData = useFlightSearchStore((state) => state.setData);
    const {locale, t} = useTranslation();

    const toggle = (selector: SelectorType) => {
        setActiveSelector(activeSelector === selector ? null : selector);
    };

    const formatDate = (date: Date | null, mode: DateSelectorMode) => {
        if (!date) return t("searchBar.placeholder");
        if (mode === DateSelectorMode.Timeline) {
            return date.toLocaleDateString(locale, {month: "long"});
        }
        return date.toLocaleDateString(locale, {day: "2-digit", month: "long"});
    };


    const formatListPreview = (items: string[]) => {
        if (items.length === 0) return t("searchBar.placeholder");
        if (items.length === 1) return items[0];
        return `${items[0]} +${items.length - 1}`;
    };

    const formatAirportListPreview = (airports: { city: string }[]) => {
        if (airports.length === 0) return t("searchBar.placeholder");
        if (airports.length === 1) return airports[0].city;
        return `${airports[0].city} +${airports.length - 1}`;
    };

    const formatDestinationPreview = () => {
        const selectedDestinations = [
            ...searchData.destinationCountries.map((country) => country.name),
            ...searchData.destinationAirports.map((airport) => airport.city),
        ];

        if (selectedDestinations.length === 0) return t("searchBar.everywhere");
        return formatListPreview(selectedDestinations);
    };

    const handleSearch = () => {
        setActiveSelector(null);
        setData(searchData);

        if (onSearch) {
            onSearch();
        } else if (window.location.pathname !== "/search") {
            navigate("/search");
        }
    };

    const isSearchEnabled =
        searchData.airports.length > 0 &&
        searchData.dateFrom !== null &&
        searchData.dateTo !== null &&
        searchData.categories.length > 0;
    const canSwapRoute =
        !!setSearchData &&
        searchData.airports.length === 1 &&
        searchData.destinationAirports.length === 1 &&
        searchData.destinationCountries.length === 0;

    const handleSwapRoute = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (!setSearchData || !canSwapRoute) return;

        const [departureAirport] = searchData.airports;
        const [destinationAirport] = searchData.destinationAirports;
        const nextSearchData = {
            ...searchData,
            airports: [destinationAirport],
            destinationAirports: [departureAirport],
            departureCountries: [],
            destinationCountries: [],
        };

        setActiveSelector(null);
        setSearchData(nextSearchData);
        setData(nextSearchData);
    };

    const primaryFieldClass =
        "cursor-pointer flex h-[68px] flex-col justify-center items-start overflow-hidden px-4 py-3 rounded-2xl w-full min-w-0 text-left bg-[#F4F2FF] hover:bg-purple-50";
    const dateFieldClass =
        "cursor-pointer flex h-[68px] flex-col justify-center items-start overflow-hidden px-4 py-3 w-full min-w-0 text-left rounded-2xl bg-gray-100 hover:bg-gray-50";

    return (
        <div className="flight-search-bar grid grid-cols-2 items-center w-full min-w-0 gap-2 bg-white rounded-2xl shadow-card p-2 max-w-4xl">
            {/* Departure Airport */}
            <button
                type="button"
                aria-expanded={activeSelector === SelectorType.Airport}
                onClick={() => toggle(SelectorType.Airport)}
                className={`${primaryFieldClass} lg:order-1`}
            >
                <span className="text-xs text-[#B1A3FF] font-bold">{t("searchBar.departure")}</span>
                <span className="text-sm font-bold text-accent block w-full truncate whitespace-nowrap">
          {formatAirportListPreview(searchData.airports)}
        </span>
            </button>

            {/* Destination */}
            <div className="relative min-w-0 lg:order-4">
                <button
                    type="button"
                    aria-expanded={activeSelector === SelectorType.Destination}
                    onClick={() => toggle(SelectorType.Destination)}
                    className={`${primaryFieldClass} ${canSwapRoute ? "pr-14" : ""}`}
                >
                    <span className="text-xs text-[#B1A3FF] font-bold">{t("searchBar.destination")}</span>
                    <span className="text-sm font-bold text-accent block w-full truncate whitespace-nowrap">
                        {formatDestinationPreview()}{searchData.travelProfile ? ` · ${t(searchData.travelProfile.visaFreeOnly ? "visa.shortFree" : searchData.travelProfile.easyVisaOnly ? "visa.shortEasy" : "visa.shortActive")}` : ""}
                    </span>
                </button>
                {canSwapRoute && (
                    <button
                        onClick={handleSwapRoute}
                        className="absolute right-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full text-accent/70 transition-smooth hover:bg-white/80 hover:text-accent"
                        title={t("searchBar.swapRoute")}
                        aria-label={t("searchBar.swapRoute")}
                    >
                        <SwapIcon/>
                    </button>
                )}
            </div>

            {/* Date from */}
            <button
                type="button"
                aria-expanded={activeSelector === SelectorType.Dates}
                onClick={() => toggle(SelectorType.Dates)}
                className={`${dateFieldClass} lg:order-2`}
            >
                <span className="text-xs text-[#BBBBBB] font-bold lg:whitespace-nowrap">{t("searchBar.dateFrom")}</span>
                <span className="text-sm font-bold text-primary block w-full truncate whitespace-nowrap">
                {formatDate(searchData.dateFrom, searchData.dateSelectorMode)}
              </span>
            </button>

            {/* Date until */}
            <button
                type="button"
                aria-expanded={activeSelector === SelectorType.Dates}
                onClick={() => toggle(SelectorType.Dates)}
                className={`${dateFieldClass} lg:order-3`}
            >
                <span className="text-xs text-[#BBBBBB] font-bold lg:whitespace-nowrap">{t("searchBar.dateTo")}</span>
                <span className="text-sm font-bold text-primary block w-full truncate whitespace-nowrap">
                {formatDate(searchData.dateTo, searchData.dateSelectorMode)}
              </span>
            </button>

            {/* Categories */}
            <button
                type="button"
                aria-expanded={activeSelector === SelectorType.Category}
                onClick={() => toggle(SelectorType.Category)}
                className={`${primaryFieldClass} lg:order-5`}
            >
                <span className="text-xs text-[#B1A3FF] font-bold">{t("searchBar.categories")}</span>
                <div className="flex items-center gap-1 text-sm font-bold text-accent w-full truncate whitespace-nowrap">
                    {formatListPreview(
                        searchData.categories.map(
                            (cat: DestinationCategory) => getCategoryLabel(cat, t)
                        )
                    )}
                </div>
            </button>

            {/* Search Button */}
            <button
                onClick={handleSearch}
                aria-label={t("searchBar.search")}
                disabled={!isSearchEnabled}
                className={`lg:order-6 flex min-h-[68px] items-center justify-center gap-2 transition text-white rounded-xl p-3 ${
                    isSearchEnabled
                        ? "bg-accent hover:bg-violet-800 cursor-pointer"
                        : "bg-violet-500 cursor-not-allowed"
                }`}
            >
                <SearchIcon/>
                <span className="lg:hidden font-bold text-sm">{t("searchBar.search")}</span>
            </button>
        </div>
    );
};
